"use client";

import React, { useState } from "react";
import { FaCode, FaCheckCircle, FaClock, FaTimesCircle, FaFileContract, FaExchangeAlt } from "react-icons/fa";
import { FiCopy, FiCheck } from "react-icons/fi";
import Jazzicon, { jsNumberForAddress } from "react-jazzicon";
import styles from "./CardList.module.css";
import { TransactionItem } from "./types";
import { truncateHash, formatValue, formatFee, formatTimeAgo } from "./utils";

interface TransactionCardProps {
  tx: TransactionItem;
  isNew: boolean;
}

const TransactionCard: React.FC<TransactionCardProps> = ({ tx, isNew }) => {
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  return (
    <div
      className={styles.card}
      style={{
        opacity: isNew ? 0 : 1,
        animation: isNew ? "fadeIn 0.5s ease-in-out forwards" : undefined,
        transition: "all 0.3s ease-in-out",
      }}
    >
      <div className={styles.cardHeader}>
        <div
          style={{
            display: "flex",
            gap: "0.5rem",
            flexWrap: "wrap",
          }}
        >
          <span className={`${styles.badge} ${styles.badgePrimary}`} style={{ display: "inline-flex", alignItems: "center", gap: "0.25rem" }}>
            <FaCode size={10} />
            {!tx.method || /^0x[0-9a-fA-F]+$/.test(tx.method) ? "Contract call" : tx.method}
          </span>
          <span
            className={`${styles.badge} ${
              tx.status === "ok" ? styles.badgeSuccess : styles.badgeWarning
            }`}
            style={{ display: "inline-flex", alignItems: "center", gap: "0.25rem" }}
          >
            {tx.status === "ok" ? <FaCheckCircle size={10} /> : tx.status === "error" ? <FaTimesCircle size={10} /> : <FaClock size={10} />}
            {tx.status === "ok" ? "Success" : tx.status || "Pending"}
          </span>
        </div>
        <span className={styles.cardTime}>{formatTimeAgo(tx.timestamp)}</span>
      </div>
      <div className={styles.cardBody}>
        <div className={styles.cardRow} style={{ marginBottom: "0.75rem" }}>
          <FaExchangeAlt size={14} className={styles.txIcon} />
          <a href={`/tx/${tx.hash}`} className={styles.cardTitle}>
            {truncateHash(tx.hash)}
          </a>
          <button
            className={styles.copyButton}
            onClick={() => copyToClipboard(tx.hash, "hash")}
          >
            {copiedField === "hash" ? <FiCheck size={12} /> : <FiCopy size={12} />}
          </button>
        </div>
        <div className={styles.cardRow}>
          <span style={{ flexShrink: 0, display: "inline-flex" }}>
            {tx.from.is_contract ? (
              <FaFileContract size={14} color="#3b82f6" />
            ) : (
              <Jazzicon diameter={16} seed={jsNumberForAddress(tx.from.hash)} />
            )}
          </span>
          <a href={`/address/${tx.from.hash}`} className={styles.hash}>
            {truncateHash(tx.from.hash)}
          </a>
          <button
            className={styles.copyButton}
            onClick={() => copyToClipboard(tx.from.hash, "from")}
          >
            {copiedField === "from" ? <FiCheck size={12} /> : <FiCopy size={12} />}
          </button>
          <span className={styles.arrow}>→</span>
          {tx.to ? (
            <>
              <span style={{ flexShrink: 0, display: "inline-flex" }}>
                {tx.to.is_contract ? (
                  <FaFileContract size={14} color="#3b82f6" />
                ) : (
                  <Jazzicon diameter={16} seed={jsNumberForAddress(tx.to.hash)} />
                )}
              </span>
              <a href={`/address/${tx.to.hash}`} className={styles.hash}>
                {truncateHash(tx.to.hash)}
              </a>
              <button
                className={styles.copyButton}
                onClick={() => copyToClipboard(tx.to!.hash, "to")}
              >
                {copiedField === "to" ? <FiCheck size={12} /> : <FiCopy size={12} />}
              </button>
            </>
          ) : (
            <span className={styles.cardValue}>Contract Creation</span>
          )}
        </div>
        <div className={styles.cardRow}>
          <span className={styles.cardLabel}>Value</span>
          <span className={styles.cardValue}>{formatValue(tx.value)}</span>
        </div>
        <div className={styles.cardRow}>
          <span className={styles.cardLabel}>Fee</span>
          <span className={styles.cardValue}>{formatFee(tx)}</span>
        </div>
      </div>
    </div>
  );
};

export default TransactionCard;
